import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import API_ENDPOINTS from '../../../config/api';
import PivotPointCurationView from '../../Lenses/PivotPointCurationView';
import PivotPointEditModal from '../../Lenses/PivotPointEditModal';
import ConfirmDialog from '../../ConfirmDialog';
import useContentRefresh from '../../../hooks/useContentRefresh';
import { useTranslation } from '../../../hooks/useTranslation';

const Wrapper = styled.div`
  height: 100%;
  min-height: 400px;
  display: flex;
  flex-direction: column;
`;

const SaveError = styled.div`
  color: #ff8a85;
  font-size: 13px;
  padding: 8px 0;
`;

/**
 * PivotPointContent - Admin pivot point curation for the orchestrator.
 * Thin wrapper around PivotPointCurationView (shared with LensesScreen).
 * Saves selectedPivotId to instance metadata so /lenses can auto-read it.
 * AI discovery is the module's admin action (manifest pivot_points.v1 →
 * "Hae vipupisteet", job pivot_points_v1.discover), run from the section's
 * action shelf; this panel refetches when the job says the pivots changed.
 *
 * `locked` prop short-circuits all writes when the workshop is locked.
 */
export default function PivotPointContent({ instanceId, locked = false }) {
  const { t } = useTranslation();
  const [pivotPoints, setPivotPoints] = useState([]);
  const [pivotEdits, setPivotEdits] = useState({});
  const [selectedPivotId, setSelectedPivotId] = useState(null);
  const [selectedPivotIds, setSelectedPivotIds] = useState(new Set());
  const [editModal, setEditModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [zeroConfirm, setZeroConfirm] = useState(null); // pending {next} when about to clear all
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState(null);
  const [clusterRefreshPendingIds, setClusterRefreshPendingIds] = useState(new Set());

  const fetchPivotPoints = useCallback(async () => {
    if (!instanceId) return;
    try {
      const res = await fetch(API_ENDPOINTS.RAG_PIVOT_POINTS(instanceId));
      if (res.ok) {
        const data = await res.json();
        const all = data.pivotPoints || data || [];
        setPivotPoints(all);
      }
    } catch (err) {
      console.error('Error fetching pivot points:', err);
    }
  }, [instanceId]);

  const fetchPivotEdits = useCallback(async () => {
    if (!instanceId) return;
    try {
      const res = await fetch(API_ENDPOINTS.PIVOT_POINT_EDITS(instanceId));
      if (res.ok) {
        const data = await res.json();
        setPivotEdits(data);
      }
    } catch (err) {
      console.error('Error fetching pivot edits:', err);
    }
  }, [instanceId]);

  // Load saved selection from instance metadata
  const loadSelectedPivot = useCallback(async () => {
    if (!instanceId) return;
    try {
      const socketUrl = API_ENDPOINTS.API_ORIGIN();
      const res = await fetch(`${socketUrl}/api/instances/${instanceId}`);
      const data = await res.json();
      if (data.metadata?.selectedPivotId) {
        setSelectedPivotId(data.metadata.selectedPivotId);
      }
      if (data.metadata?.selectedPivotIds) {
        setSelectedPivotIds(new Set(data.metadata.selectedPivotIds));
      }
    } catch (err) {
      console.error('Error loading selected pivot:', err);
    }
  }, [instanceId]);

  useEffect(() => {
    if (!instanceId) return;
    setLoading(true);
    Promise.all([fetchPivotPoints(), fetchPivotEdits(), loadSelectedPivot()])
      .finally(() => setLoading(false));
  }, [instanceId, fetchPivotPoints, fetchPivotEdits, loadSelectedPivot]);

  // The discovery job says when it has written new pivots (pivot_points_v1.discover)
  useContentRefresh(instanceId, ['pivot_points'], fetchPivotPoints, { minIntervalMs: 2000 });

  // Save selected pivot to instance metadata
  const saveSelectedPivot = useCallback(async (pivotId) => {
    if (!instanceId) return;
    try {
      const socketUrl = API_ENDPOINTS.API_ORIGIN();
      await fetch(`${socketUrl}/api/instances/${instanceId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metadata: { selectedPivotId: pivotId } }),
      });
    } catch (err) {
      console.error('Error saving selected pivot:', err);
    }
  }, [instanceId]);

  // Persist a selection set to the backend, with optimistic UI + rollback on failure.
  // Layer 1 fix: was fire-and-forget — silent 4xx/5xx losses (real incident on K4 was
  // caught only via action_log audit, not by the UI). Now awaits, checks .ok, rolls
  // back local state and surfaces the error if the save fails.
  const commitSelection = useCallback(async (next, prev) => {
    setSelectedPivotIds(next);
    setSaveError(null);
    try {
      const socketUrl = API_ENDPOINTS.API_ORIGIN();
      const res = await fetch(`${socketUrl}/api/instances/${instanceId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metadata: { selectedPivotIds: [...next] } }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error('Error saving pivot selection:', err);
      setSelectedPivotIds(prev); // rollback
      setSaveError(t('pivotPointContent.saveFailed', { error: err.message }));
    }
  }, [instanceId, t]);

  const handleSelect = (pp) => {
    if (locked) return;
    const prev = selectedPivotIds;
    const next = new Set(prev);
    if (next.has(pp.id)) next.delete(pp.id);
    else next.add(pp.id);
    // Going from N>0 to 0 wipes the workshop's lens/solution view entirely.
    // The K4 11:37 incident was exactly this — guard with explicit confirm.
    if (next.size === 0 && prev.size > 0) {
      setZeroConfirm({ next, prev });
      return;
    }
    commitSelection(next, prev);
  };

  const handleEdit = (pp) => {
    if (locked) return;
    const currentTitle = pivotEdits[pp.id]?.title_override || pp.title;
    const currentDescription = pivotEdits[pp.id]?.description_override || pp.description || '';
    setEditModal({ mode: 'edit', pivotPoint: pp, currentTitle, currentDescription });
  };

  const handleSaveEdit = async ({ title, description }) => {
    const { mode, pivotPoint } = editModal;
    setEditModal(null);

    if (mode === 'edit' && pivotPoint) {
      try {
        await fetch(API_ENDPOINTS.PIVOT_POINT_EDITS_UPSERT(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            instance_id: instanceId,
            pivot_point_id: pivotPoint.id,
            title_override: title,
            description_override: description || null,
          }),
        });
        await fetchPivotEdits();
      } catch (err) {
        console.error('Error saving pivot edit:', err);
      }
    } else if (mode === 'create') {
      try {
        await fetch(API_ENDPOINTS.RAG_PIVOT_POINT_CREATE(instanceId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title, description }),
        });
        await fetchPivotPoints();
      } catch (err) {
        console.error('Error creating pivot point:', err);
      }
    }
  };

  const handleDelete = (pp) => { if (locked) return; setDeleteConfirm(pp); };

  const executeDelete = async () => {
    const pp = deleteConfirm;
    setDeleteConfirm(null);
    try {
      const res = await fetch(API_ENDPOINTS.RAG_PIVOT_POINT_DELETE(instanceId, pp.id), { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      setPivotPoints(prev => prev.filter(p => p.id !== pp.id));
      if (selectedPivotId === pp.id) {
        setSelectedPivotId(null);
        saveSelectedPivot(null);
      }
    } catch (err) {
      console.error('Delete pivot point error:', err);
    }
  };

  const handleRefreshClusterAnalysis = useCallback(async (pp) => {
    if (!instanceId || locked || !pp?.id) return;
    setClusterRefreshPendingIds((prev) => new Set(prev).add(pp.id));
    try {
      const title = pp.title || '';
      const baseUrl = API_ENDPOINTS.LENS_PIVOT_CLUSTERS(instanceId, pp.id);
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('refresh', '1');
      if (title) url.searchParams.set('title', title);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error('Cluster analysis refresh failed:', err);
    } finally {
      setClusterRefreshPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(pp.id);
        return next;
      });
    }
  }, [instanceId, locked]);

  if (loading) return <div style={{ color: '#666', padding: 24 }}>{t('pivotPointContent.loading')}</div>;

  return (
    <Wrapper>
      {/* AI discovery is the module's admin action "Hae vipupisteet" (ModuleActionsShelf), not a panel here */}
      {saveError && <SaveError role="alert">{saveError}</SaveError>}

      <PivotPointCurationView
        pivotPoints={pivotPoints}
        edits={pivotEdits}
        selectedPivotId={selectedPivotId}
        selectedPivotIds={selectedPivotIds}
        instanceId={instanceId}
        // Pass undefined when locked → CurationView already handles missing handlers as read-only
        onEdit={locked ? undefined : handleEdit}
        onDelete={locked ? undefined : handleDelete}
        onSelect={locked ? undefined : handleSelect}
        onAdd={locked ? undefined : () => setEditModal({ mode: 'create' })}
        onRefreshClusterAnalysis={locked ? undefined : handleRefreshClusterAnalysis}
        clusterRefreshPendingIds={clusterRefreshPendingIds}
        // No onContinue/onBack — admin stays in the orchestrator
      />

      {editModal && (
        <PivotPointEditModal
          mode={editModal.mode}
          pivotPoint={editModal.pivotPoint}
          currentTitle={editModal.currentTitle}
          currentDescription={editModal.currentDescription}
          instanceTitle=""
          instanceId={instanceId}
          onSave={handleSaveEdit}
          onClose={() => setEditModal(null)}
        />
      )}

      {deleteConfirm && (
        <ConfirmDialog
          title={t('pivotPointContent.deleteTitle')}
          message={t('pivotPointContent.deleteMessage', { title: deleteConfirm.title })}
          confirmLabel={t('common.delete')}
          destructive
          onConfirm={executeDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {zeroConfirm && (
        <ConfirmDialog
          title={t('pivotPointContent.clearTitle')}
          message={t('pivotPointContent.clearMessage')}
          confirmLabel={t('pivotPointContent.clearConfirm')}
          cancelLabel={t('common.cancel')}
          destructive
          onConfirm={() => {
            const { next, prev } = zeroConfirm;
            setZeroConfirm(null);
            commitSelection(next, prev);
          }}
          onCancel={() => setZeroConfirm(null)}
        />
      )}
    </Wrapper>
  );
}
